import { PLATFORM_BASE_URL } from "../platform/config";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import React from "react";
import {
  OktonDataGrid,
  OktonDataGridColumn,
  OktonMetricCard,
  OktonPageHeader,
  useOktonAuth,
  OktonErrorState,
  OktonInput,
  OktonSelect,
} from "@okton/platform-ui";

interface PlanRow {
  id: string;
  code: number;
  active: boolean;
  name: string;
  billing_type: string;
  included_installations: number;
  included_packages: number;
  monthly_amount: number;
  implementation_amount: number;
  module_count: number;
}

interface ModuleRow {
  id: string;
  code: number;
  name: string;
  technical_module?: string;
  parent_code?: number;
  application_id?: string;
  active: boolean;
}

interface PlanDetail {
  plan: Record<string, any>;
  modules: Array<Record<string, any>>;
}

export const PlansPage: React.FC = () => {
  const { accessToken } = useOktonAuth();
  const [plans, setPlans] = React.useState<PlanRow[]>([]);
  const [modules, setModules] = React.useState<ModuleRow[]>([]);
  const [detail, setDetail] = React.useState<PlanDetail | null>(null);
  const [moduleDetail, setModuleDetail] = React.useState<Record<
    string,
    any
  > | null>(null);
  const [selectedCodes, setSelectedCodes] = React.useState<number[]>([]);
  const [moduleSearch, setModuleSearch] = React.useState("");
  const [catalogModuleSearch, setCatalogModuleSearch] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const modal = useDisclosure();
  const moduleModal = useDisclosure();
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const authHeaders = React.useMemo(
    () => ({
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    }),
    [accessToken],
  );

  const load = React.useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError("");
    try {
      const [plansResponse, modulesResponse] = await Promise.all([
        fetch(`${PLATFORM_BASE_URL}/v1/admin/catalog/plans`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: "include",
        }),
        fetch(`${PLATFORM_BASE_URL}/v1/admin/catalog/modules`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: "include",
        }),
      ]);
      const plansPayload = await plansResponse.json();
      const modulesPayload = await modulesResponse.json();
      if (!plansResponse.ok)
        throw new Error(
          plansPayload.message || "Não foi possível carregar os planos.",
        );
      if (!modulesResponse.ok)
        throw new Error(
          modulesPayload.message || "Não foi possível carregar os módulos.",
        );
      setPlans(plansPayload.data || []);
      setModules(modulesPayload.data || []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao carregar catálogo.",
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  React.useEffect(() => {
    load();
  }, [load]);

  function newPlan(): void {
    setDetail({
      plan: {
        name: "",
        active: true,
        billing_type: "PL",
        included_installations: 1,
        included_packages: 1,
        monthly_amount: 0,
        max_monthly_discount: 0,
        implementation_amount: 0,
        max_implementation_discount: 0,
        period_surcharge: 0,
        person_surcharge: 0,
        km_surcharge: 0,
      },
      modules: [],
    });
    setSelectedCodes([]);
    setModuleSearch("");
    modal.onOpen();
  }

  async function openPlan(code: number): Promise<void> {
    if (!accessToken) return;
    setError("");
    const response = await fetch(
      `${PLATFORM_BASE_URL}/v1/admin/catalog/plans/${code}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: "include",
      },
    );
    const payload = await response.json();
    if (!response.ok) {
      setError(payload.message || "Não foi possível carregar o plano.");
      return;
    }
    const data: PlanDetail = payload.data;
    setDetail(data);
    setSelectedCodes((data.modules || []).map((item) => Number(item.code)));
    setModuleSearch("");
    modal.onOpen();
  }

  function patchPlan(key: string, value: any): void {
    setDetail((current) =>
      current
        ? {
            ...current,
            plan: { ...current.plan, [key]: value },
          }
        : current,
    );
  }

  function toggleModule(code: number, checked: boolean): void {
    setSelectedCodes((current) => {
      if (checked) return Array.from(new Set([...current, code]));
      return current.filter((item) => item !== code);
    });
  }

  async function savePlan(): Promise<void> {
    if (!accessToken || !detail) return;
    setSaving(true);
    setError("");
    try {
      const existingCode = Number(detail.plan.code || 0);
      const response = await fetch(
        existingCode
          ? `${PLATFORM_BASE_URL}/v1/admin/catalog/plans/${existingCode}`
          : `${PLATFORM_BASE_URL}/v1/admin/catalog/plans`,
        {
          method: existingCode ? "PUT" : "POST",
          headers: authHeaders,
          credentials: "include",
          body: JSON.stringify(detail.plan),
        },
      );
      const payload = await response.json();
      if (!response.ok)
        throw new Error(payload.message || "Não foi possível salvar o plano.");

      const code = Number(payload.data?.plan?.code || existingCode);
      const modulesResponse = await fetch(
        `${PLATFORM_BASE_URL}/v1/admin/catalog/plans/${code}/modules`,
        {
          method: "PUT",
          headers: authHeaders,
          credentials: "include",
          body: JSON.stringify({ moduleCodes: selectedCodes }),
        },
      );
      const modulesPayload = await modulesResponse.json();
      if (!modulesResponse.ok)
        throw new Error(
          modulesPayload.message || "Não foi possível salvar os módulos.",
        );

      setDetail(modulesPayload.data);
      await load();
      modal.onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar plano.");
    } finally {
      setSaving(false);
    }
  }

  function newModule(): void {
    setModuleDetail({
      name: "",
      active: true,
      visible: true,
      parent_code: null,
      module_type: null,
      application_id: null,
    });
    moduleModal.onOpen();
  }

  async function openModule(code: number): Promise<void> {
    if (!accessToken) return;
    setError("");
    const response = await fetch(
      `${PLATFORM_BASE_URL}/v1/admin/catalog/modules/${code}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: "include",
      },
    );
    const payload = await response.json();
    if (!response.ok) {
      setError(payload.message || "Não foi possível carregar o módulo.");
      return;
    }
    setModuleDetail(payload.data);
    moduleModal.onOpen();
  }

  function patchModule(key: string, value: any): void {
    setModuleDetail((current) =>
      current ? { ...current, [key]: value } : current,
    );
  }

  async function saveModule(): Promise<void> {
    if (!accessToken || !moduleDetail) return;
    setSaving(true);
    setError("");
    try {
      const code = Number(moduleDetail.code || 0);
      const response = await fetch(
        code
          ? `${PLATFORM_BASE_URL}/v1/admin/catalog/modules/${code}`
          : `${PLATFORM_BASE_URL}/v1/admin/catalog/modules`,
        {
          method: code ? "PUT" : "POST",
          headers: authHeaders,
          credentials: "include",
          body: JSON.stringify(moduleDetail),
        },
      );
      const payload = await response.json();
      if (!response.ok)
        throw new Error(payload.message || "Não foi possível salvar o módulo.");
      setModuleDetail(payload.data);
      await load();
      moduleModal.onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar módulo.");
    } finally {
      setSaving(false);
    }
  }

  const filteredModules = modules.filter((module) => {
    const q = moduleSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      String(module.code).includes(q) ||
      module.name.toLowerCase().includes(q) ||
      String(module.technical_module || "")
        .toLowerCase()
        .includes(q)
    );
  });

  const filteredCatalogModules = modules.filter((module) => {
    const q = catalogModuleSearch.trim().toLowerCase();
    if (!q) return true;

    return (
      String(module.code).includes(q) ||
      module.name.toLowerCase().includes(q) ||
      String(module.technical_module || "")
        .toLowerCase()
        .includes(q)
    );
  });

  const planColumns: OktonDataGridColumn<PlanRow>[] = [
    { key: "code", header: "Código", value: (plan) => plan.code },
    {
      key: "name",
      header: "Plano",
      render: (plan) => <Text fontWeight="600">{plan.name}</Text>,
    },
    {
      key: "billing",
      header: "Cobrança",
      value: (plan) => plan.billing_type,
    },
    {
      key: "installations",
      header: "Instalações",
      value: (plan) => plan.included_installations,
    },
    {
      key: "packages",
      header: "Pacotes",
      value: (plan) => plan.included_packages,
    },
    {
      key: "monthly",
      header: "Mensalidade",
      value: (plan) =>
        Number(plan.monthly_amount || 0).toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        }),
    },
    {
      key: "implementation",
      header: "Implantação",
      value: (plan) =>
        Number(plan.implementation_amount || 0).toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        }),
    },
    { key: "modules", header: "Módulos", value: (plan) => plan.module_count },
    {
      key: "status",
      header: "Status",
      render: (plan) => (
        <Badge colorScheme={plan.active ? "green" : "gray"}>
          {plan.active ? "Ativo" : "Inativo"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (plan) => (
        <Button
          size="xs"
          colorScheme="blue"
          onClick={() => openPlan(plan.code)}
        >
          Editar
        </Button>
      ),
    },
  ];

  const moduleColumns: OktonDataGridColumn<ModuleRow>[] = [
    { key: "code", header: "Código", value: (module) => module.code },
    {
      key: "name",
      header: "Módulo",
      render: (module) => <Text fontWeight="600">{module.name}</Text>,
    },
    {
      key: "technical",
      header: "Módulo técnico",
      value: (module) => module.technical_module || "-",
    },
    {
      key: "parent",
      header: "Código pai",
      value: (module) => module.parent_code || "-",
    },
    {
      key: "application",
      header: "Aplicação",
      value: (module) => module.application_id || "-",
    },
    {
      key: "status",
      header: "Status",
      render: (module) => (
        <Badge colorScheme={module.active ? "green" : "gray"}>
          {module.active ? "Ativo" : "Inativo"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (module) => (
        <Button size="xs" onClick={() => openModule(module.code)}>
          Editar
        </Button>
      ),
    },
  ];

  return (
    <Box px={[4, 6, 9]} py={[6, 8, 16]} w="100%">
      <OktonPageHeader
        title="Planos e Módulos"
        subtitle="Catálogo central do ecossistema. Os códigos são os códigos funcionais oficiais."
        actions={
          <Flex gap="3" wrap="wrap">
            <Button variant="outline" onClick={newModule}>
              Novo módulo
            </Button>
            <Button colorScheme="blue" onClick={newPlan}>
              Novo plano
            </Button>
          </Flex>
        }
      />

      <SimpleGrid columns={[1, 3]} spacing="4" mb="6">
        <OktonMetricCard label="Planos" value={plans.length} />
        <OktonMetricCard label="Módulos" value={modules.length} />
        <OktonMetricCard
          label="Vínculos atuais"
          value={plans.reduce(
            (sum, plan) => sum + Number(plan.module_count || 0),
            0,
          )}
        />
      </SimpleGrid>

      {error ? (
        <OktonErrorState
          title="Não foi possível carregar o catálogo"
          description={error}
          minH="180px"
        />
      ) : (
        <OktonDataGrid
          rows={plans}
          columns={planColumns}
          getRowId={(plan) => plan.code}
          loading={loading}
          emptyMessage="Nenhum plano cadastrado. Crie um plano para iniciar o catálogo central."
          hidePagination
        />
      )}

      <Box mt="8">
        <Flex
          justify="space-between"
          align={["flex-start", "center"]}
          direction={["column", "row"]}
          mb="3"
        >
          <Box>
            <Heading size="md">Módulos</Heading>
            <Text fontSize="sm" color="gray.500">
              Cadastro central dos módulos do ecossistema.
            </Text>
          </Box>
          <OktonInput
            name="catalog-module-search"
            maxW="360px"
            mt={[3, 0]}
            placeholder="Buscar módulo por código ou nome"
            value={catalogModuleSearch}
            onChange={(e) => setCatalogModuleSearch(e.target.value)}
          />
        </Flex>
        <OktonDataGrid
          rows={filteredCatalogModules}
          columns={moduleColumns}
          getRowId={(module) => module.code}
          loading={loading}
          emptyMessage="Nenhum módulo encontrado."
          hidePagination
        />
      </Box>

      <Modal
        isOpen={modal.isOpen}
        onClose={modal.onClose}
        size="6xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {detail
              ? `Plano ${detail.plan.code} - ${detail.plan.name}`
              : "Plano"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {error && (
              <Text color="red.500" mb="4">
                {error}
              </Text>
            )}
            {detail && (
              <>
                <Heading size="sm" mb="3">
                  Dados do plano
                </Heading>
                <SimpleGrid columns={[1, 2, 3]} spacing="4">
                  <Field
                    label="Nome"
                    value={detail.plan.name}
                    onChange={(v) => patchPlan("name", v)}
                  />
                  <OktonSelect
                    name="plan-billing-type"
                    label="Tipo de cobrança"
                    value={detail.plan.billing_type || "PL"}
                    onChange={(e) => patchPlan("billing_type", e.target.value)}
                  >
                    <option value="PL">Plano</option>
                    <option value="IN">Instalações</option>
                    <option value="PC">Pacotes</option>
                  </OktonSelect>
                  <Field
                    label="Instalações incluídas"
                    type="number"
                    value={detail.plan.included_installations}
                    onChange={(v) => patchPlan("included_installations", v)}
                  />
                  <Field
                    label="Pacotes incluídos"
                    type="number"
                    value={detail.plan.included_packages}
                    onChange={(v) => patchPlan("included_packages", v)}
                  />
                  <Field
                    label="Mensalidade"
                    type="number"
                    value={detail.plan.monthly_amount}
                    onChange={(v) => patchPlan("monthly_amount", v)}
                  />
                  <Field
                    label="Desconto máx. mensalidade"
                    type="number"
                    value={detail.plan.max_monthly_discount}
                    onChange={(v) => patchPlan("max_monthly_discount", v)}
                  />
                  <Field
                    label="Implantação"
                    type="number"
                    value={detail.plan.implementation_amount}
                    onChange={(v) => patchPlan("implementation_amount", v)}
                  />
                  <Field
                    label="Desconto máx. implantação"
                    type="number"
                    value={detail.plan.max_implementation_discount}
                    onChange={(v) =>
                      patchPlan("max_implementation_discount", v)
                    }
                  />
                  <Field
                    label="Acréscimo por período"
                    type="number"
                    value={detail.plan.period_surcharge}
                    onChange={(v) => patchPlan("period_surcharge", v)}
                  />
                  <Field
                    label="Acréscimo por pessoa"
                    type="number"
                    value={detail.plan.person_surcharge}
                    onChange={(v) => patchPlan("person_surcharge", v)}
                  />
                  <Field
                    label="Acréscimo por KM"
                    type="number"
                    value={detail.plan.km_surcharge}
                    onChange={(v) => patchPlan("km_surcharge", v)}
                  />
                </SimpleGrid>
                <Flex mt="4">
                  <Checkbox
                    isChecked={Boolean(detail.plan.active)}
                    onChange={(e) => patchPlan("active", e.target.checked)}
                  >
                    Plano ativo
                  </Checkbox>
                </Flex>

                <Heading size="sm" mt="7" mb="3">
                  Módulos do plano
                </Heading>
                <OktonInput
                  name="plan-module-search"
                  placeholder="Buscar por código ou nome do módulo"
                  value={moduleSearch}
                  onChange={(e) => setModuleSearch(e.target.value)}
                  mb="3"
                />
                <Box
                  borderWidth="1px"
                  borderColor={borderColor}
                  rounded="md"
                  maxH="360px"
                  overflowY="auto"
                >
                  <Stack spacing="0">
                    {filteredModules.map((module) => (
                      <Flex
                        key={module.code}
                        px="4"
                        py="3"
                        align="center"
                        justify="space-between"
                        borderBottomWidth="1px"
                        borderColor={borderColor}
                      >
                        <Checkbox
                          isChecked={selectedCodes.includes(
                            Number(module.code),
                          )}
                          onChange={(e) =>
                            toggleModule(Number(module.code), e.target.checked)
                          }
                        >
                          <Text fontWeight="600">
                            {module.code} - {module.name}
                          </Text>
                          {module.technical_module && (
                            <Text fontSize="xs" color="gray.500">
                              {module.technical_module}
                            </Text>
                          )}
                        </Checkbox>
                        <Badge colorScheme={module.active ? "green" : "gray"}>
                          {module.active ? "Ativo" : "Inativo"}
                        </Badge>
                      </Flex>
                    ))}
                  </Stack>
                </Box>
                <Text mt="3" fontSize="sm" color="gray.500">
                  {selectedCodes.length} módulo
                  {selectedCodes.length === 1 ? "" : "s"} selecionado
                  {selectedCodes.length === 1 ? "" : "s"}.
                </Text>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="3" onClick={modal.onClose}>
              Cancelar
            </Button>
            <Button colorScheme="blue" isLoading={saving} onClick={savePlan}>
              Salvar plano
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={moduleModal.isOpen}
        onClose={moduleModal.onClose}
        size="3xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {moduleDetail?.code
              ? `Módulo ${moduleDetail.code} - ${moduleDetail.name}`
              : "Novo módulo"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {error && (
              <Text color="red.500" mb="4">
                {error}
              </Text>
            )}
            {moduleDetail && (
              <>
                <SimpleGrid columns={[1, 2]} spacing="4">
                  <Field
                    label="Nome"
                    value={moduleDetail.name}
                    onChange={(v) => patchModule("name", v)}
                  />
                  <Field
                    label="Slug"
                    value={moduleDetail.slug}
                    onChange={(v) => patchModule("slug", v)}
                  />
                  <Field
                    label="Módulo técnico"
                    value={moduleDetail.technical_module}
                    onChange={(v) => patchModule("technical_module", v)}
                  />
                  <Field
                    label="Classe"
                    value={moduleDetail.class_name}
                    onChange={(v) => patchModule("class_name", v)}
                  />
                  <Field
                    label="Código pai"
                    type="number"
                    value={moduleDetail.parent_code}
                    onChange={(v) => patchModule("parent_code", v)}
                  />
                  <Field
                    label="Nível"
                    type="number"
                    value={moduleDetail.level}
                    onChange={(v) => patchModule("level", v)}
                  />
                  <Field
                    label="Diretório/rota"
                    value={moduleDetail.directory}
                    onChange={(v) => patchModule("directory", v)}
                  />
                  <Field
                    label="Tipo de módulo"
                    type="number"
                    value={moduleDetail.module_type}
                    onChange={(v) => patchModule("module_type", v)}
                  />
                  <Field
                    label="Permissões"
                    value={moduleDetail.permissions}
                    onChange={(v) => patchModule("permissions", v)}
                  />
                  <Field
                    label="Dependências"
                    value={moduleDetail.dependencies}
                    onChange={(v) => patchModule("dependencies", v)}
                  />
                  <Field
                    label="Aplicação técnica"
                    value={moduleDetail.application_id}
                    onChange={(v) => patchModule("application_id", v)}
                  />
                </SimpleGrid>
                <Flex gap="6" mt="5" wrap="wrap">
                  <Checkbox
                    isChecked={moduleDetail.visible !== false}
                    onChange={(e) => patchModule("visible", e.target.checked)}
                  >
                    Visível
                  </Checkbox>
                  <Checkbox
                    isChecked={moduleDetail.active !== false}
                    onChange={(e) => patchModule("active", e.target.checked)}
                  >
                    Ativo
                  </Checkbox>
                </Flex>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="3" onClick={moduleModal.onClose}>
              Cancelar
            </Button>
            <Button colorScheme="blue" isLoading={saving} onClick={saveModule}>
              Salvar módulo
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

const Field: React.FC<{
  label: string;
  value: any;
  type?: string;
  onChange: (value: any) => void;
}> = ({ label, value, type = "text", onChange }) => (
  <OktonInput
    name={`plan-field-${label}`}
    label={label}
    type={type}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
  />
);
